import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { PaymentType } from '@/api/type/payment';
import { paymentTypeLabel } from '@/lib/payments';
import { LABEL_CLASS } from '@/lib/styles';
import { cn } from '@/lib/utils';

/** Cash and Online: loans given, policy payouts and balance repayments. */
export const CASH_PAYMENT_TYPES = ['Cash', 'Online'] as const;
/** Policy and loan payments can also be Paid by PMS. */
export const ALL_PAYMENT_TYPES = ['Cash', 'Online', 'PMS'] as const;

interface PaymentTypeFieldProps<T extends PaymentType> {
  id: string;
  label?: string;
  value: T;
  onChange: (value: T) => void;
  /** CASH_PAYMENT_TYPES or ALL_PAYMENT_TYPES. */
  options: readonly T[];
  /** Caption under "Cash"; giving money out reads "Handed over in person". */
  cashHint?: string;
}

/** Payment type radio cards, same look in every form. */
export function PaymentTypeField<T extends PaymentType>({
  id,
  label = 'Payment Type *',
  value,
  onChange,
  options,
  cashHint = 'Collected in person',
}: PaymentTypeFieldProps<T>) {
  const hints: Record<PaymentType, string> = {
    Cash: cashHint,
    Online: 'UPI / bank transfer',
    PMS: 'Added to member balance',
  };

  return (
    <div className="space-y-3">
      <Label className={LABEL_CLASS}>{label}</Label>
      <RadioGroup
        value={value}
        onValueChange={(next: string) => onChange(next as T)}
        className={cn('grid grid-cols-1 gap-3', options.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}
      >
        {options.map((type) => {
          const optionId = `${id}-${type}`;
          return (
            <label
              key={type}
              htmlFor={optionId}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors',
                value === type
                  ? 'border-[#4369a0]/40 bg-[#4369a0]/5 ring-1 ring-[#4369a0]/20'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
              )}
            >
              <RadioGroupItem value={type} id={optionId} className="mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-900">{paymentTypeLabel(type)}</p>
                <p className="text-xs text-gray-500">{hints[type]}</p>
              </div>
            </label>
          );
        })}
      </RadioGroup>
    </div>
  );
}
